/**
 * EmailDetailPage — the UNIFIED inbox's thread reader.
 *
 * Until now this page was a different surface from the personal reader in every
 * respect: a ghost `Back` button and an `<h2>` where the design has a nine-
 * control action bar, no star, no chips, no thread count, no prev/next, no
 * print, no open-in-provider and no footer. Both readers now mount
 * `ThreadReaderSurface` and `ThreadReaderToolbar`, so they are one surface with
 * two sets of wiring rather than two surfaces that happen to show mail.
 *
 * The wiring is genuinely different and stays different: every mail-state call
 * here carries `inbox_id`, which is what makes it a SHARED-mailbox mutation
 * (`hooks/useMailState` keys the whole distinction off that one field), and
 * Assign exists here and nowhere else because `POST /api/mail/assign` answers
 * 422 `assign_requires_inbox` without one.
 *
 * Delete is the one verb that does NOT go through `/api/mail/*` here. A shared
 * inbox's Trash is a per-inbox removal record, written by the same
 * manager-gated `DELETE /api/inboxes/{id}/messages/{id}` the list uses — see
 * `handleDelete`.
 */
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Info, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { atLeast, canReply, computeReplyRecipients, defaultFrom, fromOptions, parseFromName, replyBlockedReason, resolveReplyTarget, roleFor } from './inboxRules';
import { reportTagResult } from './BulkTagPanel';
import { conversationTagsOf, tagErrorMessage } from './MailTagPicker';
import { modeContract, threadSubject } from './mailComposerModes';
import {
    useComposeFromInbox,
    useDeleteInboxMessage,
    useDeleteOneInboxMessage,
    useInboxThread,
    useInboxTrashThread,
    usePurgeInboxMessage,
    useReplyToMessage,
    useRestoreInboxMessage,
    useInboxes,
    useInboxSenders,
} from '../../hooks/useUnifiedInbox';
import { useEmailTemplates, useSignatures } from '../../hooks/useInbox';
import { useAuth } from '../../context/AuthContext';
import { buildSignatureHtml, hasAuthoredText } from '../../lib/editorSafeHtml';
import { pickSeedSignature } from '../../lib/signatureSelection';
import { useAttachmentUpload } from '../../hooks/useAttachmentUpload';
import { resolveDisplayZone } from '../../lib/timezone';
import { useTrackingSSE } from '../../hooks/useTrackingSSE';
import { buildForwardHeader, InboxError, InboxSpinner } from './inboxUtils';
import ThreadMessage from './ThreadMessage';
import { downloadMailAttachment } from './attachmentDownload';
import ThreadReaderSurface, { ReaderChip } from './ThreadReaderSurface';
import ThreadReaderToolbar from './ThreadReaderToolbar';
import ThreadFooter from './ThreadFooter';
import ReplyComposer from './ReplyComposer';
import { SURFACES } from './surfaceContract';
import { conversationArchived, conversationBinned } from './mailRowState';
import { mailStateScopeHint, providerChip } from './originIdentity';
import useMailFolders from '../../hooks/useMailFolders';
import {
    resolveSnoozePreset,
    useArchiveMail,
    useMoveMail,
    useReadMail,
    useTagMail,
    useSnoozeMail,
    useImportantMail,
    useStarMail,
} from '../../hooks/useMailState';
import { describeSendError } from './sendErrors';
import { markMailNotificationsRead } from '../../lib/bellLinkage';
import useBellFocus from '../../hooks/useBellFocus';
import { classifyThreadError, TERMINAL_THREAD_ERRORS } from './threadLoadError';
import { inboxFolderLink, readerBackFolder, readerSearch, INBOX_FOLDER_PARAM } from './inboxDeepLink';

/**
 * #803 — what each terminal thread error tells the reader. No Retry on any of
 * them: each is the server's answer, and asking again gets the same one.
 */
const TERMINAL_COPY = {
    gone: {
        testId: 'thread-deleted',
        message: 'This email is no longer in this inbox.',
        detail: 'It was moved to Trash or deleted.',
    },
    inbox_missing: { testId: 'thread-inbox-missing', message: 'This inbox no longer exists.' },
    forbidden: { testId: 'thread-forbidden', message: 'You no longer have access to this inbox.' },
};

/** `gone` while reading this inbox's Trash (`?folder=trash`): it left the bin. */
const TRASH_GONE_COPY = {
    testId: 'thread-deleted',
    message: "This email is no longer in this inbox's Trash.",
    detail: 'It was restored or deleted for good.',
};

export default function EmailDetailPage() {
    const { inboxId, conversationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user, getAuthHeader } = useAuth();

    // Received attachments are fetched through the authenticated route and
    // handed to the browser as a blob — see `attachmentDownload.js` for why
    // this is not a plain `<a href>`.
    const downloadAttachment = useCallback(async (entry, attachment) => {
        try {
            await downloadMailAttachment({
                commId: entry.comm_id, attachment, getAuthHeader,
            });
        } catch {
            toast.error(`Could not download ${attachment.filename || 'the attachment'}`);
        }
    }, [getAuthHeader]);
    const { data: inboxes } = useInboxes();
    const inbox = useMemo(
        () => (inboxes || []).find(i => i.inbox_id === inboxId),
        [inboxes, inboxId]
    );
    // Opened from a Trash row (`?folder=trash`): read the conversation as it
    // sits in THIS inbox's Trash. The live route seeds from the inbox tag, which
    // a trashed row no longer carries — a fully trashed conversation 404'd there
    // and a partly trashed one opened on the messages still in the inbox. Both
    // queries are always declared (rules of hooks); only one is enabled.
    const inTrash = searchParams.get('folder') === 'trash';
    const liveThread = useInboxThread(inboxId, inTrash ? null : conversationId);
    const trashThread = useInboxTrashThread(inboxId, conversationId, { enabled: inTrash });
    const { data: thread, isLoading, isError, error: threadError, refetch } = inTrash ? trashThread : liveThread;
    // #803 — gone / inbox deleted / access lost are answers, not blips: the
    // reader stops showing AND acting on the rows React Query kept from the
    // last good fetch. A `failed` load (5xx, network, the feature-flag 404)
    // keeps them: that thread still exists, and Retry is the honest offer.
    const threadFailure = classifyThreadError(threadError);
    const threadTerminal = TERMINAL_THREAD_ERRORS.has(threadFailure);
    const terminalCopy = threadFailure === 'gone' && inTrash
        ? TRASH_GONE_COPY : TERMINAL_COPY[threadFailure];
    // Back from a thread that left: to this inbox, in the folder it was read
    // from (Trash included); from a deleted inbox or a lost grant, to the inbox
    // root.
    const backFolder = readerBackFolder(searchParams);
    const terminalBack = threadFailure !== 'gone' ? '/inbox' : inboxFolderLink(inboxId, backFolder);
    const reply = useReplyToMessage(inboxId);
    // #659 — reply-all starts a NEW thread (the prototype's "New thread"), so
    // it is sent as a new message through the inbox's compose endpoint, never
    // the reply one, which would thread it with In-Reply-To/References.
    const compose = useComposeFromInbox(inboxId);
    const [body, setBody] = useState('');
    const [from, setFrom] = useState(null);
    // #659 — the From picker is in the reader FOOTER now, visible before any
    // composer opens. Once the user has picked an address there, neither the
    // preselect effect nor opening a composer may overwrite it. Reset per
    // thread, below.
    const fromPickedRef = useRef(false);
    const pickFrom = useCallback((value) => {
        fromPickedRef.current = true;
        setFrom(value);
    }, []);
    // #597 — the signature currently in the body: { id, html }. Owned HERE, not
    // inside TemplateSignatureSelectors, because the seed below and the send
    // guard further down both need the exact HTML that was inserted, and two
    // copies of that would be two sources of truth (validation finding V1).
    const [signature, setSignature] = useState({ id: '', html: '' });
    // #623 — attachments. Owned here for the same reason the signature is: the
    // SEND path needs them, and it lives here.
    const attachmentState = useAttachmentUpload();
    // A single target controls all inline composers. The page-level composer is
    // hidden while this is set, so two drafts can never be visible together.
    const [openComposerFor, setOpenComposerFor] = useState(null); // { commId, mode, name }
    const [reconnectRequired, setReconnectRequired] = useState(false);
    // Phase 3: Optimistic message state — synthetic "Sending..." card
    const [optimisticMessage, setOptimisticMessage] = useState(null);
    // Phase 4: Editable To/CC/BCC recipients
    const [to, setTo] = useState([]);
    const [cc, setCc] = useState([]);
    const [bcc, setBcc] = useState([]);
    const [showCcBcc, setShowCcBcc] = useState(false);

    // Phase 3: Mount SSE listener on the detail route so inbox_message events
    // trigger thread refetch. /inbox and /inbox/:inboxId/:conversationId are
    // sibling top-level routes — React Router unmounts Inbox (closing the
    // EventSource) before mounting EmailDetailPage, so no duplicate connection.
    useTrackingSSE();

    // #597 — called ONCE here, at the page's top level. ReplyComposer renders
    // inside messagesWithBodies.map(...) below, so calling these there would
    // violate the rules of hooks (craco.config.js sets that to `error`, which
    // fails `yarn build` — the Docker image build) and would multiply each
    // hook's mount-time invalidateQueries by the thread length.
    const { data: templatesData } = useEmailTemplates();
    const { data: signaturesData } = useSignatures();

    const ownAddress = user?.email;
    // Narrowed by the server — see `useInboxSenders`. Without it the Reply-as
    // picker offered pending and revoked addresses whose send is refused.
    const { sendableAddresses } = useInboxSenders(inboxId);
    const options = useMemo(
        () => fromOptions(inbox, user, { ownAddress, sendableAddresses }),
        [inbox, user, ownAddress, sendableAddresses]
    );
    // Loading guard: canReply returns false when inbox is undefined (still
    // loading via useInboxes), which is indistinguishable from a real denial
    // until the data resolves. Defer the permission check until inbox loads.
    const mayReply = inbox ? canReply(inbox, user, { ownAddress }) : null;
    const realMessages = useMemo(
        () => (!threadTerminal && thread?.data?.length ? thread.data : []),
        [thread?.data, threadTerminal]
    );

    // F13/F14: Shared time-proximity match — single source of truth for both
    // the useMemo (display) and useEffect (clear).  Guards against NaN from
    // undefined/invalid comm_date (new Date(undefined).getTime() === NaN),
    // which would silently fail the match and leave the "Sending..." card stuck.
    const matchesOptimistic = useCallback((m, opt) => {
        if (!m || !opt) return false;
        if (m.direction !== 'outbound') return false;
        if (m.from_address !== opt.from_address) return false;
        if (m.__synthetic) return false;
        const realTs = new Date(m.comm_date).getTime();
        const optTs = new Date(opt.comm_date).getTime();
        if (Number.isNaN(realTs) || Number.isNaN(optTs)) return false;
        return Math.abs(realTs - optTs) < 60000;
    }, []);

    const messages = useMemo(() => {
        if (!optimisticMessage) return realMessages;
        // Match on from_address + direction + time proximity (within 60s),
        // NOT body_html — Graph normalizes/wraps HTML so byte comparison fails.
        const realMatch = realMessages.find(m => matchesOptimistic(m, optimisticMessage));
        if (realMatch) return realMessages;
        const targetIndex = optimisticMessage.reply_to_comm_id
            ? realMessages.findIndex(m => m.comm_id === optimisticMessage.reply_to_comm_id)
            : -1;
        if (targetIndex < 0) return [...realMessages, optimisticMessage];
        return [
            ...realMessages.slice(0, targetIndex + 1),
            optimisticMessage,
            ...realMessages.slice(targetIndex + 1),
        ];
    }, [realMessages, optimisticMessage, matchesOptimistic]);

    // Clear the optimistic message once the real data has replaced it.
    useEffect(() => {
        if (!optimisticMessage) return;
        const realMatch = realMessages.find(m => matchesOptimistic(m, optimisticMessage));
        if (realMatch) setOptimisticMessage(null);
    }, [realMessages, optimisticMessage, matchesOptimistic]);

    // Safety net: clear the optimistic message after 35 seconds regardless
    // of match logic, so the "Sending..." card can never get permanently stuck.
    // 35s = 30s backend overarching deadline + 5s buffer for network latency,
    // ensuring the backend always responds (or errors) before the card clears.
    useEffect(() => {
        if (!optimisticMessage) return;
        const timer = setTimeout(() => setOptimisticMessage(null), 35000);
        return () => clearTimeout(timer);
    }, [optimisticMessage]);
    // Synthetic sending cards are display-only and must never become the target
    // of the page-level fallback composer.
    const latest = [...messages].reverse().find(message => !message.__synthetic) || null;

    // Reset the tracked signature alongside the body: leaving a stale value
    // pointing at HTML no longer present would make the next switch append.
    const clearComposerDraft = useCallback(() => {
        setBody('');
        setSignature({ id: '', html: '' });
        setTo([]);
        setCc([]);
        setBcc([]);
        setShowCcBcc(false);
    }, []);

    // The one close-and-clear, for ✕ / Discard, a re-press of the same
    // control, a successful send and a thread change. A stale draft,
    // signature or file left behind would surface in the next composer.
    const handleCloseComposer = useCallback(() => {
        setOpenComposerFor(null);
        clearComposerDraft();
        attachmentState.reset();
    }, [attachmentState, clearComposerDraft]);

    // Phase 3: Clear optimistic message when navigating away or thread changes.
    // #659 — also drop the previous thread's composer, draft and picked From.
    // Declared BEFORE the preselect effect on purpose: effects run in
    // declaration order, and on an already-cached thread both run in the same
    // commit, so the preselect must see the reset rather than the stale pick.
    useEffect(() => {
        setOptimisticMessage(null);
        fromPickedRef.current = false;
        handleCloseComposer();
    }, [inboxId, conversationId]); // eslint-disable-line react-hooks/exhaustive-deps -- per-thread reset: it must fire on a thread change and on nothing else

    // Pre-select the From address that matches the email's recipient (Gmail pattern)
    // The footer's "Replying as" should default to the address that received the email,
    // not just the first option or the distro address.
    useEffect(() => {
        if (openComposerFor || !options.length || isLoading) return;
        if (fromPickedRef.current) return;
        // Derive from the last INBOUND message — the address the customer wrote
        // to is the one they should hear back from. `latest` is often our own
        // outbound reply, whose recipients are the customer, so matching against
        // it could never find one of our own send-as options.
        const lastInbound = [...messages].reverse().find(m => m.direction !== 'outbound');
        const addressedTo = [
            ...((lastInbound || latest)?.to_emails || []),
            ...((lastInbound || latest)?.cc_emails || []),
            ...(((lastInbound || latest)?.recipients || []).map(r => r.mail)),
        ].filter(Boolean);
        const recipientMatch = options.find(opt => addressedTo.includes(opt.address));
        setFrom(recipientMatch ? recipientMatch.address : defaultFrom(options));
    }, [openComposerFor, options, latest, messages, isLoading]);

    // UX-6: 'R' keyboard shortcut for reply (V2-UX4: ref instead of querySelector)
    const replyEditorRef = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            if (e.key.toLowerCase() !== 'r') return;
            const tag = e.target?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            e.preventDefault();
            const editor = replyEditorRef.current?.querySelector('[contenteditable]');
            if (editor) editor.focus();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);

    // V2-UX3: Auto-scroll to latest message on thread open (Gmail pattern).
    //
    // This used to walk UP the tree hunting for a scrollable ancestor, because
    // the reader had no scroll region of its own and relied on the shell's page
    // scrollport. `ThreadReaderSurface` owns one now and hands the node back
    // through `scrollRef`, so the search is gone — along with the failure mode
    // where a new wrapper between the page and `<main>` silently swallowed it.
    //
    //
    // #715 — a bell entry is ONE message, so when the bell opened this thread
    // (`location.state.focusCommId`) `useBellFocus` scrolls to that message
    // instead, once, and falls back to this newest-message scroll otherwise —
    // including for every later growth of the thread (a sent reply, new mail).
    const threadScrollRef = useRef(null);
    const scrollToNewest = useCallback((node) => {
        node.scrollTop = node.scrollHeight;
    }, []);
    useBellFocus({
        scrollRef: threadScrollRef,
        threadKey: `${inboxId}/${conversationId}`,
        ready: !isLoading,
        count: messages.length,
        onNoFocus: scrollToNewest,
    });

    /**
     * #597 — the body no longer starts empty; it starts with the signature.
     *
     * `sendingAddress` must be the address computed by the CALLER in this same
     * tick, never the `from` state: `setFrom` above has not re-rendered yet, so
     * reading `from` here would bake the PREVIOUS thread's mailbox into
     * {{SenderEmail}} — a live #487 regression.
     *
     * buildSignatureHtml is the SAME recipe TemplateSignatureSelectors uses
     * (resolveSigVars → toEditorSafeHtml). Both sides must produce byte-identical
     * HTML or the component's strip will miss this seed and the first switch
     * will stack a second signature.
     */
    const seedSignature = useCallback((sendingAddress) => {
        const seed = pickSeedSignature({
            activeOverrideId: signaturesData?.activeOverrideId,
            signatures: signaturesData?.signatures,
            adminSignatures: signaturesData?.adminSignatures,
            defaultSignature: signaturesData?.defaultSignature,
        });
        const html = seed ? buildSignatureHtml(seed, user, sendingAddress || '') : '';
        setSignature({ id: seed && html ? seed.signature_id : '', html });
        setBody(html ? `<p></p><p></p>${html}` : '');
    }, [signaturesData, user]);

    const handleOpenComposer = useCallback((entry, mode) => {
        const isSameComposer = openComposerFor?.commId === entry.comm_id
            && openComposerFor?.mode === mode;
        if (isSameComposer) {
            handleCloseComposer();
            return;
        }

        const addressedTo = [
            ...(entry.to_emails || []),
            ...(entry.cc_emails || []),
            ...((entry.recipients || []).map(recipient => recipient.mail)),
        ].filter(Boolean);
        const recipientMatch = options.find(option =>
            addressedTo.some(address => address.toLowerCase() === option.address.toLowerCase())
        );
        // Our OWN message is answered from the address that sent it, when that
        // is still on offer — not from whatever its recipients happen to match.
        const isOutbound = entry.direction === 'outbound';
        const sentFrom = isOutbound
            ? options.find(option => option.address.toLowerCase()
                === String(entry.from_address || '').toLowerCase())
            : null;
        // A From the user picked in the footer wins over the derived default,
        // as long as it is still on offer.
        const chosenFrom = fromPickedRef.current && options.some(option => option.address === from)
            ? from
            : (sentFrom?.address || recipientMatch?.address || defaultFrom(options));

        // Reply All must never send a copy back to the mailbox that is sending
        // the reply. "Own" is the signed-in user PLUS the From this composer
        // will ACTUALLY use (computed above, not the previous `from` state) —
        // NOT every option the admin can switch to: including all options
        // filtered every member of a pooled inbox out of the recipient list
        // when an admin opened the composer. For our own outbound message its
        // sender is ours too, so an alias it was copied to is not us twice.
        const ownAddresses = [ownAddress, chosenFrom, isOutbound ? entry.from_address : null]
            .filter(Boolean);

        // #659 — a plain reply has NO To row, so its recipient is resolved
        // here, before anything opens. With nobody to reply to, refuse rather
        // than open a composer that cannot send.
        const target = mode === 'reply'
            ? resolveReplyTarget(entry, { ownAddresses, displayName: parseFromName })
            : null;
        if (target?.reason) {
            toast.error(target.reason);
            return;
        }
        const recipients = computeReplyRecipients(entry, {
            mode,
            ownAddresses,
        });
        setFrom(chosenFrom);
        setTo(recipients.to.map(email => ({ email })));
        setCc(recipients.cc.map(email => ({ email })));
        setBcc(recipients.bcc.map(email => ({ email })));
        // Open the CC/BCC toggle automatically when either field has content,
        // so the BCC recipients the user just opted into are visible without
        // an extra click. Reply (single recipient) never auto-opens.
        setShowCcBcc(recipients.cc.length > 0 || recipients.bcc.length > 0);
        // #597 REVERSES the previous decision here. This used to read: "Outlook-
        // style: the composer body starts empty … rather than a silent default
        // that fills the editor with content the user did not ask for." The
        // issue's acceptance criteria require the signature to be present when
        // the composer opens, on every surface — a rep should not have to pick
        // their own signature on every reply. Quoting the original is still a
        // separate affordance and is still not done here; the provider appends
        // the quote after send.
        //
        // Seeded from `chosenFrom`, NOT the `from` state — see seedSignature.
        seedSignature(chosenFrom);
        setReconnectRequired(false);
        // A different composer opening must not inherit the last one's files.
        attachmentState.reset();
        setOpenComposerFor({ commId: entry.comm_id, mode, name: target?.name || null });
    }, [attachmentState, from, handleCloseComposer, openComposerFor, options, ownAddress, seedSignature]);

    const handleReply = useCallback(entry => handleOpenComposer(entry, 'reply'), [handleOpenComposer]);
    const handleReplyAll = useCallback(entry => handleOpenComposer(entry, 'reply_all'), [handleOpenComposer]);



    // #659 — reply-all's subject, derived ONCE from the thread's subject: the
    // card shows it read-only and the compose payload sends it, so the two
    // cannot disagree. Null for a mode with no subject row.
    const newThreadSubject = openComposerFor
        ? threadSubject(modeContract(openComposerFor.mode), messages[0]?.subject)
        : null;

    // C2/H8: Reply targets the clicked card's message with user-edited To/CC/BCC.
    // Phase 3: Creates optimistic "Sending..." message before sending.
    // The composer only exists when openComposerFor is set, so the target is
    // always the clicked card — no page-level fallback to fall back to.
    const submit = async () => {
        const target = openComposerFor
            ? messages.find(message => message.comm_id === openComposerFor.commId)
            : null;
        // #597 — the body now opens PRE-SEEDED with a signature, so a bare
        // `!body.trim()` check can never fail and a signature-only reply would
        // go out on a stray click. Anything the user typed still counts.
        if (!hasAuthoredText(body)) { toast.error('Write a reply first'); return; }
        // #623 — never send over an unfinished or failed upload. Surfacing the
        // failure in the composer is pointless if the send goes anyway.
        if (attachmentState.pendingCount) { toast.error('Wait for attachments to finish uploading'); return; }
        if (attachmentState.failedCount) { toast.error('Remove or retry the attachments that failed to upload'); return; }
        if (!target) { toast.error('No message to reply to'); return; }
        if (!to.length) { toast.error('Please add at least one recipient'); return; }

        const reportSendError = (err) => {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            const detailCode = typeof detail === 'string' ? detail : detail?.code;
            // Keep the departmental reconnect side effect, but centralise the
            // user-facing classification with PersonalThreadView. This avoids
            // the two thread pages drifting on the same provider error.
            if (detailCode === 'reconnect_required') {
                setReconnectRequired(true);
            }
            toast.error(
                describeSendError(err),
                status === 502 || status === 503 ? { autoClose: 8000 } : undefined
            );
        };
        const recipients = {
            to: to.map(c => c.email),
            cc: cc.map(c => c.email),
            bcc: bcc.map(c => c.email),
        };

        // #659 — a new-thread mode (reply all) is a NEW message: the compose
        // endpoint, the read-only subject the card showed, and no optimistic
        // card in THIS thread, because it will not land in this thread.
        if (modeContract(openComposerFor.mode).threadBehaviour === 'new-thread') {
            try {
                await compose.mutateAsync({
                    ...recipients,
                    subject: newThreadSubject,
                    body,
                    body_type: 'HTML',
                    from_address: from,
                    ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
                });
                toast.success(`Sent to everyone as a new thread, from ${from}`);
                handleCloseComposer();
            } catch (err) {
                reportSendError(err);
            }
            return;
        }

        try {
            const payload = {
                messageId: target.comm_id,
                conversationId,  // F06: Per-thread polling scoping
                body,
                body_type: 'HTML',
                from_address: from,
                // Phase 4: Use user-edited recipients, converted from {email} to string
                ...recipients,
                // Staged object keys, not bytes — the server fetches them.
                ...(attachmentState.payload.length && { attachments: attachmentState.payload }),
            };

            // Phase 3: Create optimistic message BEFORE sending
            const syntheticMsg = {
                comm_id: `synthetic-${Date.now()}`,
                __synthetic: true,
                from_address: from,
                from_name: user?.name || user?.email || from,
                to_emails: payload.to,
                cc_emails: payload.cc,
                bcc_emails: payload.bcc,
                subject: target.subject || messages[0]?.subject || '(No subject)',
                body: body,
                body_html: body,
                comm_date: new Date().toISOString(),
                direction: 'outbound',
                conversation_id: target.conversation_id || conversationId,
                reply_to_comm_id: target.comm_id,
            };
            setOptimisticMessage(syntheticMsg);

            await reply.mutateAsync(payload);
            toast.success(`Reply sent as ${from}`);
            // #659 — CLOSE the card, as the personal lane does. It used to stay
            // open with its recipients cleared; with no To row on the anchored
            // card that left a composer that could never send again.
            handleCloseComposer();
            // The optimistic message stays until the real refetch replaces it
        } catch (err) {
            setOptimisticMessage(null);  // Remove optimistic on failure
            reportSendError(err);
        }
    };

    // Forward a specific card through the existing compose route. Router state
    // avoids putting message HTML in the URL and ComposePage already consumes
    // these two fields to bypass draft restoration.
    const handleForward = useCallback((entry) => {
        if (!entry) return;
        const forwardHeader = buildForwardHeader({
            fromAddress: entry.from_address,
            fromName: parseFromName(entry),
            date: entry.comm_date,
            subject: entry.subject,
            bodyHtml: entry.body_html,
            body: entry.body,
            zone: resolveDisplayZone({ user }),
        });
        navigate(`/inbox/${inboxId}/compose`, {
            state: {
                forwardedSubject: `Fwd: ${entry.subject || ''}`,
                forwardedBody: forwardHeader,
            },
        });
    }, [inboxId, navigate]);

    // ── Mail state ───────────────────────────────────────────────────────────
    //
    // Every target here carries `inbox_id`. That is not decoration: it is the
    // ONLY thing that tells `/api/mail/*` this is a shared-mailbox mutation
    // rather than one reader's private state, and dropping it would silently
    // write personal-lane rows for a thread several people can see.
    const starMail = useStarMail();
    const importantMail = useImportantMail();
    const readMail = useReadMail();
    const archiveMail = useArchiveMail();
    const snoozeMail = useSnoozeMail();
    const moveMail = useMoveMail();
    const { data: folderModel } = useMailFolders(inboxId);
    // Delete removes mail from the SHARED view, so it is the list's manager
    // verb (`surfaceContract.rowActionsFor`'s `canDelete`), not a responder's
    // triage like the rest of this block. Below manager the toolbar Delete,
    // Move to → Trash and the per-message Delete render disabled.
    const mayDelete = atLeast(roleFor(inbox, user), 'manager');
    const deleteConversation = useDeleteInboxMessage(inboxId);
    const deleteOneMessage = useDeleteOneInboxMessage(inboxId);
    // Trash mode's two doors out of the bin — the list's Trash row verbs, the
    // same manager floor (`surfaceContract.rowActionsFor`).
    const restoreConversation = useRestoreInboxMessage(inboxId);
    const purgeConversation = usePurgeInboxMessage(inboxId);
    const [confirmPurge, setConfirmPurge] = useState(false);
    useEffect(() => { setConfirmPurge(false); }, [conversationId]);

    const threadIds = useMemo(
        () => messages.map(m => m.comm_id).filter(id => id && !String(id).startsWith('synthetic-')),
        [messages]
    );
    const target = useMemo(
        () => ({ ids: threadIds, inbox_id: inboxId }),
        [threadIds, inboxId]
    );
    // #721 — which way the Archive control writes. Opened from Archived it is
    // Move to Inbox; a static Archive there was a no-op that toasted success.
    const archived = useMemo(() => conversationArchived(messages), [messages]);
    // Whether Move to → Inbox is a restore (a bin) or a no-op (archived, not in a
    // bin): the toolbar withholds it in the second case.
    const binned = useMemo(() => conversationBinned(messages), [messages]);
    // Every verb that changes a live conversation is off in Trash: archiving,
    // snoozing or tagging a message that is in the bin would file it in two
    // places at once — the rule `rowActionsFor` states for the Trash list.
    const actionable = threadIds.length > 0 && !inTrash;

    // #646 — tags on shared mail need the responder grant (the server enforces
    // it; this only decides whether to offer the controls). The toolbar tags
    // the conversation; a message's ⋯ menu tags that message.
    const tagMail = useTagMail();
    const mayTag = atLeast(roleFor(inbox, user), 'responder');
    const conversationTags = useMemo(() => conversationTagsOf(messages), [messages]);
    const changeTags = useCallback(
        async (ids, { add = [], remove = [] }, expand) => {
            if (!ids.length || (!add.length && !remove.length)) return;
            try {
                const res = await tagMail.mutateAsync({
                    target: { ids: ids.slice(0, 200), inbox_id: inboxId },
                    add,
                    remove,
                    expand_conversations: expand,
                });
                reportTagResult(res?.data, { verb: 'Tags updated', noun: 'message' });
            } catch (err) {
                toast.error(tagErrorMessage(err));
            }
        },
        [tagMail, inboxId]
    );

    // #715 D1 opener rule — opening the thread clears the OPENER's bell
    // entries for its messages. Deliberately NOT a mail mark-read: read state
    // on a pooled inbox is shared, and one member opening a thread is not a
    // decision for the rest of the inbox. Each id is posted at most once per
    // mount (audit F11): a refetch, or a new row such as the reply just sent,
    // posts only the ids not posted yet.
    const bellPostedIdsRef = useRef(new Set());
    useEffect(() => {
        const fresh = threadIds.filter(id => !bellPostedIdsRef.current.has(id));
        if (!fresh.length) return;
        fresh.forEach(id => bellPostedIdsRef.current.add(id));
        markMailNotificationsRead(fresh, getAuthHeader);
    }, [threadIds, getAuthHeader]);

    const goBack = useCallback(() => {
        // M-Navigation: prefer browser back; fall back to URL with filters.
        // F11 fix: idx > 0, because window.history.state is always truthy
        // under BrowserRouter.
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);
            return;
        }
        // V2-UX1 / C5: preserve the list's filter state on back navigation.
        const backParams = new URLSearchParams();
        backParams.set('inbox', searchParams.get('inbox') || inboxId);
        // `mailbox` was missing from this list, so Back through the FALLBACK
        // path (no history entry — a deep link, a refresh, a new tab) returned
        // to the inbox with the platform switcher's selection silently reset.
        // The history path preserved it because it replays the URL; the two
        // paths disagreed, which is why it looked intermittent (#696).
        ['search', 'sender', 'date_from', 'date_to', 'replied', 'sort', 'dir', 'page',
         'mailbox']
            .forEach(key => {
                const val = searchParams.get(key);
                if (val) backParams.set(key, val);
            });
        // The rail folder the list was in. It rode in on the list's click
        // (`readerSearch`); a link that predates that knows only `folder=trash`.
        // The reader's own `folder` is NOT forwarded: the unified surface
        // ignores it, and the personal surface — rendered while inboxes load —
        // would read it as the user's OWN Trash.
        if (backFolder) backParams.set(INBOX_FOLDER_PARAM, backFolder);
        navigate(`/inbox?${backParams.toString()}`);
    }, [navigate, searchParams, inboxId, backFolder]);

    // Starring a CONVERSATION stars its messages, so the list row — built from
    // the thread's root — flips too. Reading it back as "any message starred"
    // is the same rule in reverse; the two halves have to agree or the star
    // appears to undo itself on the next refetch.
    const [starOverride, setStarOverride] = useState(null);
    const threadStarred = starOverride ?? messages.some(m => m.is_starred === true);
    // The Important marker, mirroring the star exactly: an optimistic
    // override so the icon answers the click, reconciled from the thread on
    // the next fetch. "Any message important" is the same rule the list row
    // reads, so the two cannot disagree about a thread's state.
    const [importantOverride, setImportantOverride] = useState(null);
    const threadImportant =
        importantOverride ?? messages.some(m => m.is_important === true);
    const handleImportant = () => {
        if (!threadIds.length) return;
        const next = !threadImportant;
        setImportantOverride(next);
        importantMail.mutate(
            { target: target, important: next },
            {
                onError: () => {
                    setImportantOverride(!next);
                    toast.error("Couldn't update Important");
                },
            }
        );
    };

    const handleStar = () => {
        if (!threadIds.length) return;
        const next = !threadStarred;
        setStarOverride(next);
        starMail.mutate({ target, starred: next }, {
            onError: () => { setStarOverride(!next); toast.error("Couldn't update the star"); },
        });
    };

    const handleMarkUnread = () => {
        if (!threadIds.length) return;
        readMail.mutate({ target, read: false }, {
            onSuccess: () => { toast.success('Marked as unread'); goBack(); },
            onError: () => toast.error("Couldn't mark this conversation unread"),
        });
    };

    const handleArchive = () => {
        if (!threadIds.length) return;
        archiveMail.mutate({ target, archived: !archived }, {
            onSuccess: (res) => {
                // `onSuccess` fires on any 2xx, so a write that changed nothing
                // must not report success — the same rule the row strip and the
                // bulk bar follow (#710).
                if (res?.data?.modified === 0) {
                    toast.warning('Nothing changed — the message may have moved already');
                    return;
                }
                toast.success(archived ? 'Moved to Inbox' : 'Conversation archived');
                goBack();
            },
            onError: () => toast.error(archived
                ? "Couldn't move this conversation to Inbox"
                : "Couldn't archive this conversation"),
        });
    };

    // Delete moves the conversation to THIS inbox's Trash, the same move the
    // list row's Delete makes. It is not `/mail/move {folder: 'trash'}`, which
    // is what this page used to send: that flags the shared ROW, so the message
    // vanished from every inbox it was tagged into, appeared in no Trash, and
    // the 30-day sweep then deleted it for everyone. The server refuses it now.
    //
    // Seeded with the URL's `conversationId`, the same seed the thread fetch
    // expands, so what is removed is exactly what this page is showing.
    const handleDelete = () => {
        if (!threadIds.length) return;
        deleteConversation.mutate(conversationId, {
            onSuccess: () => {
                toast.success('Conversation moved to Trash');
                goBack();
            },
            onError: () => toast.error("Couldn't move this conversation to Trash"),
        });
    };

    // Spam and Inbox only: the toolbar sends Trash to `handleDelete`. The
    // guard is for any other caller, so a stray 'trash' still takes the right
    // route rather than the one the server refuses.
    const handleMove = (folder) => {
        if (folder === 'trash') {
            if (mayDelete) handleDelete();
            return;
        }
        if (!threadIds.length) return;
        moveMail.mutate({ target, folder }, {
            onSuccess: (res) => {
                // `onSuccess` fires on any 2xx; a move that changed nothing must
                // not report success (the same rule as Archive above).
                if (res?.data?.modified === 0) {
                    toast.warning('Nothing changed — the message may have moved already');
                    return;
                }
                toast.success(folder ? `Moved to ${folder}` : 'Moved to Inbox');
                goBack();
            },
            onError: () => toast.error("Couldn't move this conversation"),
        });
    };

    // One message, all of its mailbox copies, into this inbox's Trash. The rest
    // of the conversation stays; the reader leaves only when nothing is left.
    const handleDeleteMessage = (entry) => {
        if (!entry?.comm_id) return;
        const wasLast = threadIds.length <= 1;
        // The URL seeds the thread fetch, and a row with no `conversation_id`
        // opens on its own comm_id (the list's fallback). Removing THAT message
        // would leave the refetch nothing to seed from — a 404 while the rest of
        // the conversation is still in the inbox — so re-seed on one that stays.
        const copies = entry.tag_comm_ids?.length ? entry.tag_comm_ids : [entry.comm_id];
        const seedLeaves = copies.includes(conversationId)
            || entry.internet_message_id === conversationId;
        const survivor = messages.find(m =>
            m.comm_id !== entry.comm_id && !String(m.comm_id).startsWith('synthetic-'));
        deleteOneMessage.mutate(entry.comm_id, {
            onSuccess: () => {
                toast.success('Message moved to Trash');
                if (wasLast) {
                    goBack();
                } else if (seedLeaves && survivor) {
                    navigate(
                        {
                            pathname: `/inbox/${inboxId}/${survivor.conversation_id || survivor.comm_id}`,
                            search: location.search,
                        },
                        { replace: true, state: location.state },
                    );
                }
            },
            onError: () => toast.error("Couldn't move this message to Trash"),
        });
    };

    // Restore re-tags exactly what the Trash view shows (`_trashed_thread_rows`
    // is both the read and the restore), then leaves, like every verb here.
    const handleRestore = () => {
        restoreConversation.mutate(conversationId, {
            onSuccess: () => {
                toast.success('Restored to this inbox');
                goBack();
            },
            onError: () => toast.error("Couldn't restore this conversation"),
        });
    };

    // Irreversible for THIS inbox, so it asks once, inline. Other inboxes and
    // the contact's history keep the message — the purge drops only this
    // inbox's removal record.
    const handlePurge = () => {
        setConfirmPurge(false);
        purgeConversation.mutate(conversationId, {
            onSuccess: () => {
                toast.success('Deleted from this inbox');
                goBack();
            },
            onError: () => toast.error("Couldn't delete this conversation"),
        });
    };

    const handleSnooze = (presetKey) => {
        // Resolved HERE, in the viewer's DISPLAY zone, and sent as an absolute
        // instant — the API accepts no symbolic preset by design. A second
        // wall-clock interpretation in this tree is the #578/#540 defect class.
        const until = resolveSnoozePreset(presetKey, new Date(), resolveDisplayZone({ user }));
        if (!until || !threadIds.length) return;
        snoozeMail.mutate({ target, until: until.toISOString() }, {
            onSuccess: () => { toast.success('Snoozed'); goBack(); },
            onError: () => toast.error("Couldn't snooze this conversation"),
        });
    };

    // ── Reader identity ──────────────────────────────────────────────────────
    const anchor = messages[0] || null;
    const anchorOrigin = anchor?.origin || null;
    // On this lane the mailbox chip names the SHARED inbox, not a personal
    // address: it is the thing several people can see, and the thing every
    // mutation above is scoped to.
    const receivingAddress = inbox?.distro_smtp || anchor?.mailbox_address || null;

    // Optional list context handed over by the row that opened this thread.
    // Absent on a deep link or a refresh — and then the counter does NOT render
    // and prev/next disable, rather than a position guessed from whichever page
    // of rows happens to be cached.
    const threadNav = location.state?.threadNav || null;
    // ‹ › open the neighbouring thread from the same list, so they carry what
    // this URL carries: the Trash read, and the folder Back leads to.
    const neighbourQuery = readerSearch({
        inTrash,
        listFolder: searchParams.get(INBOX_FOLDER_PARAM),
    });

    // "Forward all — whole thread" forwards the WHOLE thread. Forwarding only
    // the newest message under that label is the same class of lie as #596's
    // dead link: the control says what it does, so it has to do it.
    const handleForwardAll = useCallback(() => {
        if (!messages.length) return;
        const zone = resolveDisplayZone({ user });
        const composed = messages
            .map(entry => buildForwardHeader({
                fromAddress: entry.from_address,
                fromName: parseFromName(entry),
                date: entry.comm_date,
                subject: entry.subject,
                bodyHtml: entry.body_html,
                body: entry.body,
                zone,
            }))
            .join('<hr />');
        navigate(`/inbox/${inboxId}/compose`, {
            state: {
                forwardedSubject: `Fwd: ${messages[0]?.subject || ''}`,
                forwardedBody: composed,
            },
        });
    }, [messages, navigate, inboxId, user]);


    // #659 — where the one open composer renders is the mode contract's call.
    const composerAnchored = openComposerFor ? modeContract(openComposerFor.mode).anchored : false;
    const threadLevelTarget = openComposerFor && !composerAnchored
        ? messages.find(message => message.comm_id === openComposerFor.commId) || null
        : null;

    // One function, two render sites (the message slot and the thread-level
    // slot), so they cannot drift. A plain function returning JSX — not a
    // component and not a hook — so the rules of hooks are untouched.
    const renderComposer = (message) => (
        <ReplyComposer
            key={`${message.comm_id}:${openComposerFor.mode}`}
            composerId={message.comm_id}
            mode={openComposerFor.mode}
            // F1 — without these the header read the literal "Reply to
            // {name}". `name` is resolveReplyTarget's, so a reply to our own
            // message names its recipient, not us.
            modeVars={{
                name: openComposerFor.name || parseFromName(message) || message.from_address || '',
                subject: messages[0]?.subject || '',
                n: messages.length,
            }}
            to={to}
            setTo={setTo}
            cc={cc}
            setCc={setCc}
            bcc={bcc}
            setBcc={setBcc}
            showCcBcc={showCcBcc}
            setShowCcBcc={setShowCcBcc}
            body={body}
            setBody={setBody}
            reconnectRequired={reconnectRequired}
            // The mutation that is actually in flight for this mode.
            reply={composerAnchored ? reply : compose}
            onSubmit={submit}
            onClose={handleCloseComposer}
            subject={newThreadSubject}
            editorRef={replyEditorRef}
            // #597 — fetched ONCE at this page's top level and passed down;
            // the composer renders inside a .map() and never fetches.
            templates={templatesData?.templates}
            adminTemplates={templatesData?.adminTemplates}
            signatures={signaturesData?.signatures}
            adminSignatures={signaturesData?.adminSignatures}
            activeOverrideId={signaturesData?.activeOverrideId}
            user={user}
            // The mailbox actually sending. On this lane `from` IS an address
            // (fromOptions / defaultFrom), unlike the personal lane, which
            // passes a provider id (#484). A footer From change re-resolves
            // the signature through this (F8).
            senderEmail={from || ''}
            signature={signature}
            onSignatureChange={setSignature}
            attachments={attachmentState.attachments}
            onAddAttachments={attachmentState.addAttachments}
            onRemoveAttachment={attachmentState.remove}
        />
    );

    const messageCount = messages.length;
    const blockedReason = mayReply === false
        ? (replyBlockedReason(inbox, user, { ownAddress }) || 'You cannot reply in this inbox.')
        : null;

    return (
        <ThreadReaderSurface
            testId="email-detail-page"
            scrollRef={threadScrollRef}
            busy={isLoading}
            subject={isLoading ? 'Loading…' : threadTerminal ? '' : (messages[0]?.subject || '(No subject)')}
            starred={threadStarred}
            onStar={actionable ? handleStar : undefined}
            important={threadImportant}
            onImportant={inTrash ? undefined : handleImportant}
            starTitle={mailStateScopeHint(anchorOrigin, threadStarred ? 'Unstar' : 'Star') || undefined}
            threadCount={messageCount
                ? `${messageCount} message${messageCount !== 1 ? 's' : ''} in this thread`
                : null}
            toolbar={
                <ThreadReaderToolbar
                    surface={SURFACES.unified}
                    origin={anchorOrigin}
                    onBack={goBack}
                    onArchive={actionable ? handleArchive : undefined}
                    archived={archived}
                    binned={binned}
                    onDelete={actionable && mayDelete ? handleDelete : undefined}
                    onMarkUnread={actionable ? handleMarkUnread : undefined}
                    onSnooze={actionable ? handleSnooze : undefined}
                    onMove={actionable ? handleMove : undefined}
                    folderModel={folderModel}
                    /* Assign is drawn (SURFACES.unified.supportsAssign) but
                       carries no handler, so it renders disabled: the endpoint
                       exists, the assignee PICKER does not, and a button that
                       opens nothing is worse than one that says it cannot yet.
                       #646 owns the picker. */
                    position={threadNav?.position}
                    total={threadNav?.total}
                    /* ‹ › walk the list the reader was opened from; from Trash
                       that is the Trash list, whose rows only the Trash read
                       can open. */
                    onPrev={threadNav?.prevId
                        ? () => navigate(`/inbox/${inboxId}/${threadNav.prevId}${neighbourQuery}`)
                        : undefined}
                    onNext={threadNav?.nextId
                        ? () => navigate(`/inbox/${inboxId}/${threadNav.nextId}${neighbourQuery}`)
                        : undefined}
                    onPrint={() => window.print()}
                    conversationTags={conversationTags}
                    onChangeConversationTags={mayTag && actionable
                        ? (delta) => changeTags(threadIds, delta, true)
                        : undefined}
                />
            }
            chips={
                <span className="flex items-center gap-[7px] min-w-0" data-testid="thread-mailbox-pill">
                    {providerChip(anchorOrigin) && (
                        <ReaderChip testId="thread-provider-chip">
                            {providerChip(anchorOrigin)}
                        </ReaderChip>
                    )}
                    {receivingAddress && (
                        <ReaderChip testId="thread-receiving-address">
                            {receivingAddress}
                        </ReaderChip>
                    )}
                </span>
            }
            footer={threadTerminal ? null : (
                inTrash ? (
                    /* Trash mode: nothing here can be replied to or filed, so
                       the footer is the bin's two doors — for a manager, the
                       same floor the Trash list gives them. */
                    <div
                        className="flex-shrink-0 px-5 py-4 border-t border-[#EDEBE6] bg-stone-50"
                        data-testid="inbox-trash-footer"
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <Info className="w-4 h-4 text-stone-500 flex-shrink-0" />
                            <p className="flex-1 min-w-0 text-xs text-stone-600">
                                In this inbox's Trash, and deleted from it after 30 days.
                                Other inboxes and the contact's history keep it.
                                {mayDelete ? ' Restore it to reply.' : ' Ask an inbox manager to restore it.'}
                            </p>
                            {mayDelete && (confirmPurge ? (
                                <span className="flex items-center gap-2">
                                    <span className="text-xs text-red-700">Delete it from this inbox for good?</span>
                                    <Button
                                        size="sm"
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                        onClick={handlePurge}
                                        disabled={purgeConversation.isPending}
                                        data-testid="inbox-trash-purge-confirm"
                                    >
                                        Delete forever
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setConfirmPurge(false)}
                                        data-testid="inbox-trash-purge-cancel"
                                    >
                                        Cancel
                                    </Button>
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        onClick={handleRestore}
                                        disabled={!messageCount || restoreConversation.isPending}
                                        data-testid="inbox-trash-restore"
                                    >
                                        Restore
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setConfirmPurge(true)}
                                        disabled={!messageCount}
                                        data-testid="inbox-trash-purge"
                                    >
                                        Delete forever
                                    </Button>
                                </span>
                            ))}
                        </div>
                    </div>
                ) : blockedReason ? (
                    /* Read-only surfaces get an explanation where the reply
                       controls would be, not a row of disabled buttons — the
                       user cannot act here at all, and the actionable thing is
                       the role request, which is in this text. */
                    <div
                        className="flex-shrink-0 px-5 py-4 border-t border-[#EDEBE6] bg-blue-50"
                        data-testid="inbox-reply-blocked"
                    >
                        <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-blue-700">
                                <p className="font-medium">{blockedReason}</p>
                                <p className="mt-0.5">
                                    You can read this thread but not reply. Ask an inbox manager to raise
                                    your role to Responder in{' '}
                                    <span
                                        className="underline cursor-pointer"
                                        onClick={() => navigate('/admin?tab=unified-inbox')}
                                    >
                                        Settings › Inbox
                                    </span>.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <ThreadFooter
                        // `latest`, not the last row: an optimistic
                        // "Sending…" card can be last, and it is not a
                        // message anyone can reply to.
                        onReplyAll={latest && mayReply ? () => handleReplyAll(latest) : undefined}
                        onForwardAll={messageCount && mayReply ? handleForwardAll : undefined}
                        replyingAs={from || null}
                        // #659 D3 — the From choice, moved out of the card.
                        // No `identityState`: an inbox with no sendable
                        // address swaps this footer for the blocked
                        // explanation above.
                        fromOptions={options}
                        fromValue={from}
                        onFromChange={pickFrom}
                    />
                )
            )}
        >
            {isLoading ? (
                <InboxSpinner label="Loading thread…" />
            ) : threadTerminal ? (
                <div data-testid={terminalCopy.testId}>
                    <InboxError message={terminalCopy.message} detail={terminalCopy.detail} />
                    <div className="flex justify-center">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(terminalBack)}
                        >
                            Back to inbox
                        </Button>
                    </div>
                </div>
            ) : isError ? (
                /* V2-UX2: Error handling with retry button (Gmail pattern) */
                <div data-testid="thread-error">
                    <InboxError message="Failed to load this thread." onRetry={refetch} />
                    <div className="flex justify-center">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/inbox?inbox=${inboxId}`)}>
                            Back to inbox
                        </Button>
                    </div>
                </div>
            ) : messages.length === 0 ? (
                /* M-Loading: Empty thread state */
                <div className="flex flex-col items-center justify-center py-12 gap-2 text-stone-400" data-testid="thread-empty">
                    <Mail className="w-8 h-8" />
                    <p className="text-sm">This thread has no messages</p>
                </div>
            ) : (
                <>
                    {messages.map(message => (
                        <ThreadMessage
                            key={message.comm_id}
                            entry={message}
                            inbox={inbox}
                            canReplyAllowed={mayReply === true && !inTrash}
                            onReply={handleReply}
                            onReplyAll={handleReplyAll}
                            onForward={handleForward}
                            onDelete={mayDelete && !inTrash && !String(message.comm_id).startsWith('synthetic-')
                                ? handleDeleteMessage
                                : undefined}
                            getAuthHeader={getAuthHeader}
                            onDownloadAttachment={downloadAttachment}
                            onMarkUnread={actionable ? handleMarkUnread : undefined}
                            onChangeTags={mayTag && !inTrash && !String(message.comm_id).startsWith('synthetic-')
                                // Every per-mailbox copy of the message (`tag_comm_ids`), so the
                                // list — which unions over copies — agrees with the thread.
                                ? (msg, delta) => changeTags(msg.tag_comm_ids?.length ? msg.tag_comm_ids : [msg.comm_id], delta, false)
                                : undefined}
                            // #659 — only the ANCHORED mode (reply) renders in
                            // the replied-to message's slot.
                            composer={openComposerFor?.commId === message.comm_id && composerAnchored
                                ? renderComposer(message)
                                : null}
                        />
                    ))}
                    {/* #659 D2 — Reply all is a THREAD-level composer, after the
                        last message, never inside one. Same single
                        `openComposerFor`, so it and the anchored card are
                        mutually exclusive. */}
                    {threadLevelTarget && renderComposer(threadLevelTarget)}
                </>
            )}
        </ThreadReaderSurface>
    );
}
